import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getMotifs } from '@/api/directSale';
import { useImageOperations } from '@/hooks/useImageOperations';
import { useDesignStore } from '@/stores/designStore';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { cn } from '@/lib/utils';

/**
 * The motif drawer of the desktop editor: the phone panel's data and search
 * in the shape of Spreadshirt's designer — a tall search field, a heading
 * over the grid, tiles on the second paper tone. Clicking a motif places it
 * on the active print position and closes the drawer.
 *
 * The motif id doubles as the placed object's assetId (its file endpoint is
 * permanent), so the object counts as a completed asset — placed with a
 * `temp_` id it would be silently dropped by the serializer's
 * filterTempAssets and never persist or unlock the checkout.
 *
 * Search filters client-side across both language names — the library is
 * curated and small; a server round-trip per keystroke buys nothing.
 */
export function DirectSaleDesktopMotifPanel({
  onClose,
}: {
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [placeFailed, setPlaceFailed] = useState(false);
  // Which tile is fetching: the spinner belongs on the clicked motif, not
  // on the whole grid — the customer should see what is coming.
  const [placingId, setPlacingId] = useState<string | null>(null);
  const { addImageFromUrl, isLoading } = useImageOperations();
  const isPlacing = isLoading || placingId !== null;

  const {
    data: motifs,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['direct-sale', 'motifs'],
    queryFn: getMotifs,
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return motifs ?? [];
    return (motifs ?? []).filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        m.nameEn.toLowerCase().includes(term)
    );
  }, [motifs, search]);

  const handlePick = async (motifId: string, fileUrl: string) => {
    setPlaceFailed(false);
    setPlacingId(motifId);
    // The image fetch can take seconds; if the customer switches the print
    // position meanwhile, the resolved canvas.add would land on (and bill)
    // the NEW position — capture the origin and undo a stale placement.
    const originPosition = useProductSelectionStore.getState().activePosition;
    const placed = await addImageFromUrl(fileUrl, motifId, {
      placement: 'print-area-fitted',
    });
    setPlacingId(null);

    if (!placed) {
      setPlaceFailed(true);
      return;
    }

    const currentPosition = useProductSelectionStore.getState().activePosition;
    if (currentPosition !== originPosition) {
      // Cross-store canvas access — a cleanup failure must not break the
      // panel (cf. docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md).
      try {
        const canvas = useDesignStore.getState().canvas;
        if (canvas) {
          canvas.remove(placed);
          canvas.discardActiveObject();
          canvas.requestRenderAll();
        }
      } catch {
        // The stray object stays; the customer can delete it manually.
      }
      setPlaceFailed(true);
      return;
    }

    onClose();
  };

  const motifName = (motif: { name: string; nameEn: string }) =>
    i18n.language === 'en' ? motif.nameEn : motif.name;

  return (
    <div className="flex flex-1 flex-col px-6 py-4">
      <div className="flex h-12 items-center gap-2 rounded-control border border-line px-4 text-mute focus-within:border-primary">
        <Search className="h-4 w-4 flex-none" aria-hidden="true" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('directSale.design.motifPanel.search')}
          className="h-full w-full bg-transparent text-sm text-ink-2 outline-none placeholder:text-mute"
        />
      </div>

      <h3 className="mt-5 text-sm font-bold uppercase tracking-wide text-ink">
        {t('directSale.design.motifs')}
      </h3>

      {isPending && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="aspect-square animate-pulse rounded-card bg-paper-2"
            />
          ))}
        </div>
      )}

      {isError && (
        <div className="mt-3">
          <p className="text-xs text-ink-2">
            {t('directSale.design.motifPanel.loadFailed')}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 h-9 rounded-control border border-line px-3 text-xs font-semibold text-ink-2 transition-colors hover:bg-paper-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            {t('directSale.product.retry')}
          </button>
        </div>
      )}

      {!isPending && !isError && (
        <>
          {filtered.length === 0 ? (
            <p className="mt-3 text-xs text-mute">
              {search.trim()
                ? t('directSale.design.motifPanel.empty')
                : t('directSale.design.motifPanel.none')}
            </p>
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {filtered.map((motif) => {
                const isThisPlacing = placingId === motif.id;
                return (
                  <button
                    key={motif.id}
                    type="button"
                    disabled={isPlacing}
                    aria-busy={isThisPlacing}
                    title={motifName(motif)}
                    aria-label={motifName(motif)}
                    onClick={() => void handlePick(motif.id, motif.fileUrl)}
                    className={cn(
                      'relative flex aspect-square items-center justify-center rounded-card border border-line bg-paper-2 transition-colors hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed',
                      isPlacing && !isThisPlacing && 'opacity-60'
                    )}
                  >
                    <img
                      src={motif.fileUrl}
                      alt={motifName(motif)}
                      crossOrigin="anonymous"
                      loading="lazy"
                      // A thumb dragged onto the canvas would only raise the
                      // editor's file drop overlay; clicking is the way in.
                      draggable={false}
                      className="h-full w-full object-contain p-3"
                    />
                    {isThisPlacing && (
                      <span
                        aria-hidden="true"
                        data-testid="motif-placing-overlay"
                        className="absolute inset-0 flex items-center justify-center rounded-card bg-paper-3 text-primary"
                      >
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {placeFailed && (
        <p role="alert" className="mt-3 text-xs font-medium text-danger">
          {t('directSale.design.motifPanel.placeFailed')}
        </p>
      )}

      <p className="mt-4 border-t border-line pt-4 text-xs text-mute">
        {t('directSale.design.motifPanel.license')}
      </p>
    </div>
  );
}
